import React, { useEffect, useState } from "react";
import { Mail, Phone, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { api, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const STATUSES = ["New", "In Progress", "Completed"];

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState(null);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/admin/inquiries").then(({ data }) => setInquiries(data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  const setStatus = async (inq, status) => {
    try {
      await api.patch(`/admin/inquiries/${inq.id}`, { status });
      setInquiries(inquiries.map((i) => (i.id === inq.id ? { ...i, status } : i)));
      toast.success(`Marked as ${status}`);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const remove = async (inq) => {
    if (!window.confirm(`Delete the inquiry from ${inq.name}? This cannot be undone.`)) return;
    await api.delete(`/admin/inquiries/${inq.id}`);
    toast.success("Inquiry deleted");
    load();
  };

  const statusColor = (s) => ({
    New: "bg-amber-100 text-amber-800",
    "In Progress": "bg-blue-100 text-blue-800",
    Completed: "bg-emerald-100 text-emerald-800",
  }[s] || "bg-slate-100 text-slate-600");

  return (
    <div data-testid="admin-inquiries">
      <div className="mb-6">
        <h2 className="font-heading text-3xl font-semibold text-[#0B1D3A]">Customer Inquiries</h2>
        <p className="text-slate-500 text-sm">Messages from your website's contact form. Only you can see this page.</p>
      </div>

      {inquiries === null ? (
        <div className="py-16 flex justify-center"><div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-600 rounded-full animate-spin" /></div>
      ) : inquiries.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500" data-testid="inquiries-empty">
          No inquiries yet. When visitors send a message through your Contact page, it will appear here and be emailed to you.
        </div>
      ) : (
        <div className="space-y-3">
          {inquiries.map((inq) => (
            <div key={inq.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`inquiry-row-${inq.id}`}>
              <div className="flex items-center gap-3 px-4 py-3.5 flex-wrap">
                <button onClick={() => setOpen(open === inq.id ? null : inq.id)} className="flex items-center gap-2 flex-1 min-w-[200px] text-left" aria-expanded={open === inq.id} data-testid={`inquiry-toggle-${inq.id}`}>
                  {open === inq.id ? <ChevronDown className="w-4 h-4 text-amber-600" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  <span className="font-semibold text-[#0B1D3A]">{inq.name}</span>
                  <span className="text-xs text-slate-400">{inq.inquiry_type}</span>
                </button>
                <span className="text-xs text-slate-400 hidden sm:block">{new Date(inq.created_at).toLocaleString()}</span>
                <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${statusColor(inq.status)}`} data-testid={`inquiry-status-${inq.id}`}>{inq.status}</span>
                <select value={inq.status} onChange={(e) => setStatus(inq, e.target.value)} aria-label="Change status" data-testid={`inquiry-status-toggle-${inq.id}`}
                  className="text-xs rounded-lg border border-slate-300 px-2 py-1.5 bg-white">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={() => remove(inq)} aria-label="Delete inquiry" data-testid={`inquiry-delete-${inq.id}`} className="p-2 rounded-lg hover:bg-red-50"><Trash2 className="w-4 h-4 text-red-500" /></button>
              </div>
              {open === inq.id && (
                <div className="border-t border-slate-100 px-5 py-4 bg-slate-50/60">
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm mb-3">
                    <a href={`mailto:${inq.email}`} className="inline-flex items-center gap-1.5 text-amber-700 font-medium"><Mail className="w-3.5 h-3.5" /> {inq.email}</a>
                    {inq.phone && <span className="inline-flex items-center gap-1.5 text-slate-600"><Phone className="w-3.5 h-3.5" /> {inq.phone}</span>}
                  </div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid={`inquiry-message-${inq.id}`}>{inq.message}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
